import React from 'react'
import { Utils } from 'core/utils';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import { useQuery, useQueryClient } from 'react-query';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import CategoryForm from './forms/CategoryForm';
import { JobHttpService } from '../service';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    Stack,
    CircularProgress,
    Alert,
    TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

function CategoryList() {
    const queryClient = useQueryClient();
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = React.useState(false);
    const [addDialogIsOpen, setAddDialogIsOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');

    const { data, isLoading, isError, refetch } = useQuery('categories', () => JobHttpService.loadCategories(), {
        onSuccess: (data) => {
            // Handle success
        }
    });

    const categories = Array.isArray(data) ? data : (data?.results || []);

    const filteredCategories = categories.filter(category =>
        category.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const _handleDelete = (e, id) => {
        e.stopPropagation();
        setSelectedId(id);
        setDeleteDialogIsOpen(true);
    }

    const _onDeleteDone = () => {
        setDeleteDialogIsOpen(false);
        queryClient.invalidateQueries('categories');
        setSelectedId(null);
    }

    const _handleEdit = (e, id) => {
        e.stopPropagation();
        setSelectedId(id);
        setAddDialogIsOpen(true);
    }

    const _handleAdd = () => {
        setSelectedId(null);
        setAddDialogIsOpen(true);
    }

    const _handleFormDone = () => {
        refetch();
        queryClient.invalidateQueries('categories');
        setAddDialogIsOpen(false);
        setSelectedId(null);
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight={600} color="text.primary">
                    Categories
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={_handleAdd}
                >
                    Add New Category
                </Button>
            </Stack>

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search categories..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    />
                </Box>

                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : isError ? (
                    <Box p={3}>
                        <Alert severity="error">Failed to load categories. Please try again.</Alert>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>ID</strong></TableCell>
                                    <TableCell><strong>Title</strong></TableCell>
                                    <TableCell><strong>Image</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Created At</strong></TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCategories.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {searchTerm ? 'No categories found matching your search.' : 'No categories found. Add one to get started.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredCategories.map((item) => (
                                        <TableRow key={item.id} hover>
                                            <TableCell>{item.id}</TableCell>
                                            <TableCell>{Utils.normalizeText(item.title)}</TableCell>
                                            <TableCell>
                                                {item.image ? (
                                                    <Box
                                                        component="img"
                                                        src={item.image}
                                                        alt={item.title}
                                                        sx={{
                                                            width: 50,
                                                            height: 50,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            border: '1px solid',
                                                            borderColor: 'divider'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = '/assets/images/placeholder.png';
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No image
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.status ? 'Active' : 'Inactive'}
                                                    color={item.status ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{Utils.getDate(item.created_at)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={(e) => _handleEdit(e, item.id)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => _handleDelete(e, item.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <DeleteDialog
                url={`/job/category/${selectedId}`}
                isOpen={deleteDialogIsOpen}
                key={"mdl" + deleteDialogIsOpen + selectedId}
                setOpen={setDeleteDialogIsOpen}
                notifyDone={_onDeleteDone}
            />

            <CustomModal
                big
                className=""
                key={addDialogIsOpen}
                setOpen={(value) => setAddDialogIsOpen(value)}
                open={addDialogIsOpen}
                title={selectedId ? "Edit Category" : "Add New Category"}
            >
                <CategoryForm id={selectedId} onEditDone={_handleFormDone} />
            </CustomModal>
        </Box>
    )
}

export default CategoryList

